import React, { useState, useEffect, useMemo } from 'react';
import Header from './components/Header';
import AppGrid from './components/AppGrid';
import AppInfoModal from './components/AppInfoModal';
import RequestSolutionModal from './components/RequestSolutionModal';
import SuggestionModal from './components/SuggestionModal';
import AdminPanel from './components/AdminPanel';
import Login from './components/Login';
import { getDashboardData } from './services/dataService';
import authService from './services/authService';
import './App.css';

function App() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'analytics', or 'admin'
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isSuggestionModalOpen, setIsSuggestionModalOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState({ apps: [], categories: [], analytics: null });
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check for authentication callback on app load
  useEffect(() => {
    checkAuthCallback();
  }, []);

  // Load dashboard data from backend
  useEffect(() => {
    if (authChecked && isAuthenticated) {
      loadDashboardData();
    }
  }, [authChecked, isAuthenticated]);

  const checkAuthCallback = async () => {
    try {
      // Check if this is an auth callback
      const urlParams = new URLSearchParams(window.location.search);
      const authCode = urlParams.get('code');
      const state = urlParams.get('state');

      if (authCode) {
        // Handle the auth callback
        let redirectUri = `${window.location.origin}/auth/callback`;
        
        // Force HTTPS for Azure App Service or production environments
        if (window.location.hostname.includes('azurewebsites.net') || 
            window.location.protocol !== 'https:' && 
            window.location.hostname !== 'localhost') {
          redirectUri = redirectUri.replace('http://', 'https://');
        }
        
        await authService.handleCallback(authCode, redirectUri);

        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);

        // Show success message
        // alert('Login successful!');
        setIsAuthenticated(true);
        setIsAdmin(authService.isAdmin);
      } else {
        // Validate existing token
        const isValid = await authService.validateToken();
        setIsAuthenticated(isValid);
        setIsAdmin(authService.isAdmin);
      }
    } catch (error) {
      console.error('Auth callback failed:', error);
      // alert('Login failed. Please try again.');
      setIsAuthenticated(false);
    }

    setAuthChecked(true);
  };

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      const data = await getDashboardData();
      setDashboardData(data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      // Fallback to empty data structure
      setDashboardData({
        apps: [],
        categories: [],
        analytics: { dashboardStats: {}, popularApps: [], categoryUsage: [], monthlyTrends: [] }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDataUpdated = () => {
    loadDashboardData();
  };

  // Filter apps based on search term and selected category
  const filteredApps = useMemo(() => {
    return dashboardData.apps.filter(app => {
      const matchesSearch = searchTerm === '' || 
        app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesCategory = selectedCategory === '' || 
        app.category.id === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [dashboardData.apps, searchTerm, selectedCategory]);

  // Separate featured and regular apps
  const featuredApps = useMemo(() => {
    return filteredApps.filter(app => app.featured);
  }, [filteredApps]);

  const regularApps = useMemo(() => {
    return filteredApps.filter(app => !app.featured);
  }, [filteredApps]);

  const handleAppClick = async (app) => {
    try {
      // Send analytics data to backend
      await fetch('/api/analytics/track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          app_name: app.name,
          category: app.category.name,
          url: app.url,
          event_type: 'app_click',
          page: 'dashboard'
        })
      });
    } catch (error) {
      console.error('Failed to track analytics:', error);
    }

    // Open the app URL
    window.open(app.url, '_blank', 'noopener,noreferrer');
  };

  const handleInfoClick = (app) => {
    setSelectedApp(app);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedApp(null);
  };

  const handleViewAdmin = () => {
    if (authService.canAccessAdmin()) {
      setCurrentView('admin');
    } else {
      // This shouldn't happen if UI is correct, but just in case
      alert('Admin access denied. You do not have permission to access the admin panel.');
    }
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
  };

  const handleRequestSolution = () => {
    setIsRequestModalOpen(true);
  };

  const handleCloseRequestModal = () => {
    setIsRequestModalOpen(false);
  };

  const handleMakeSuggestion = () => {
    setIsSuggestionModalOpen(true);
  };

  const handleCloseSuggestionModal = () => {
    setIsSuggestionModalOpen(false);
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setIsAdmin(authService.isAdmin);
  };

  // Show login screen if not authenticated
  if (!authChecked) {
    return <div className="app">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  // Show admin panel if user is admin and requested it
  if (currentView === 'admin' && isAuthenticated && isAdmin) {
    return (
      <div className="app">
        <AdminPanel
          onBackToDashboard={handleBackToDashboard}
          onDataUpdated={handleDataUpdated}
        />
      </div>
    );
  }

  // Main dashboard view
  
  return (
    <div className="app">
      <Header
        onSearch={setSearchTerm}
        searchTerm={searchTerm}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        categories={dashboardData.categories}
        onViewAdmin={handleViewAdmin}
        currentView={currentView}
        showAdminButton={isAuthenticated && isAdmin}
      />

      <main className="main-content">
        <div className="container">
          {!isLoading && (
            <div className="stats-bar">
              <div className="stats-left">
                <div className="stats-item">
                  <span className="stats-number">{filteredApps.length}</span>
                  <span className="stats-label">
                    {filteredApps.length === 1 ? 'Application' : 'Applications'}
                  </span>
                </div>
                <div className="stats-item">
                  <span className="stats-number">{dashboardData.categories.length}</span>
                  <span className="stats-label">Categories</span>
                </div>
                <div className="stats-item">
                  <span className="stats-number">{featuredApps.length}</span>
                  <span className="stats-label">Featured</span>
                </div>
              </div>
              <div className="stats-right">
                <button
                  onClick={handleMakeSuggestion}
                  className="suggestion-btn"
                  title="Share your idea - All suggestions will be reviewed and prioritized"
                >
                  Make a Suggestion
                </button>
                <button
                  onClick={handleRequestSolution}
                  className="request-solution-btn"
                  title="Request a new AI solution for your department"
                >
                  Help Us Automate! Submit a Request
                </button>
              </div>
            </div>
          )}

          {featuredApps.length > 0 && !isLoading && (
            <section className="featured-section">
              <h2 className="section-title">⭐ Featured Applications</h2>
              <AppGrid
                apps={featuredApps}
                onAppClick={handleAppClick}
                onInfoClick={handleInfoClick}
                isLoading={false}
              />
            </section>
          )}

          {regularApps.length > 0 && !isLoading && (
            <section className="apps-section">
              <h2 className="section-title">
                {featuredApps.length > 0 ? '🚀 All Applications' : '🚀 Your Applications'}
              </h2>
              <AppGrid
                apps={regularApps}
                onAppClick={handleAppClick}
                onInfoClick={handleInfoClick}
                isLoading={false}
              />
            </section>
          )}

          {isLoading && (
            <AppGrid
              apps={[]}
              onAppClick={handleAppClick}
              isLoading={true}
            />
          )}

          {filteredApps.length === 0 && !isLoading && (
            <AppGrid
              apps={[]}
              onAppClick={handleAppClick}
              isLoading={false}
            />
          )}
        </div>
      </main>

      <AppInfoModal
        app={selectedApp}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />

      <RequestSolutionModal
        isOpen={isRequestModalOpen}
        onClose={handleCloseRequestModal}
      />

      <SuggestionModal
        isOpen={isSuggestionModalOpen}
        onClose={handleCloseSuggestionModal}
      />

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <p>&copy;2025 Knipper Health - All Rights Reserved.</p>
            <div className="footer-links">
              <span>Total Apps: {dashboardData.apps.length}</span>
              <span>•</span>
              <span>Categories: {dashboardData.categories.length}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;